import { useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { Button } from '@voll-intelligence/ui-kit/ui/button';
import {
  SCREEN_BAR_HEIGHT,
  ScreenBar,
  ScreenBarCenter,
  ScreenBarEnd,
  ScreenBarRow,
  ScreenBarStart,
} from '@voll-intelligence/ui-kit/ui/screen-bar';
import {
  HotelSearchBar,
  HotelSummaryPill,
  namedGuestCount,
  type HotelSearchBarProps,
  type HotelSummary,
} from '@voll-intelligence/intelligence-ui-kit';
import { useGenUiConfig } from '../../../shared/ui/config.js';
import { useGenUiI18n } from '../../../shared/ui/i18n.js';
import { ScreenDialog } from '@voll-intelligence/ui-kit/ui/screen-dialog';
import { ScreenBarClose, useScreenBarTrail } from '../../../shared/ui/screen_bar_parts.js';
import { hasForegroundDialog } from './foreground_dialog.js';
import {
  HotelMapDialog,
  type fullscreenPortalProps,
  type ResultsDialogLifecycleSlice,
  type ResultsDialogSharedProps,
} from './hotel_map_dialog.js';
import { DeterministicResultsPage } from './hotel_results_page.js';
import type { ExpiredSearchRetry } from './use_expired_search_retry.js';

/*
 * A TELA EM DIÁLOGO "Ver todos os hotéis" da conversa — a MESMA página determinística
 * de resultados da bancada (`DeterministicResultsPage` → `HotelResultsPage`,
 * #2994: título, meta + ordenador, coluna de cortes, cards empilhados, mapa ao
 * lado; no estreito o palco map-centric), por cima da conversa inteira, na
 * anatomia da casca do explorer de voos (`FlightExplorerDialog`): a barra do
 * topo é o ✕ que sai, a pill de contexto da busca (ocupação · destino · datas
 * + a lupa que re-busca o MESMO pedido) e, no canto direito, o `trailing` do
 * host (a mala da viagem, #3301). Substituiu o painel lateral próprio
 * (`HotelAllResults`, apagado): eram duas geometrias para a mesma lista, e a
 * do chat divergia da bancada em cada slot — mapa no topo da coluna, sem h1,
 * contagem e ordenador abaixo do mapa, a faixa de busca aberta no cabeçalho.
 *
 * A pill NO LUGAR da faixa de busca: no shell a bancada mostra a pill e um
 * clique volta ao formulário; aqui não há formulário para voltar, então o
 * clique EXPANDE a `HotelSearchBar` no lugar dela (o análogo do planner inline
 * da pill do voo) — Buscar re-busca e recolhe, Cancelar/Esc só recolhe.
 *
 * Foco, trap, Esc e a devolução do foco são do Base UI (`ScreenDialog`); o
 * aberto/fechado mora no organismo, e este arquivo recebe a fatia que usa,
 * como o mapa cheio.
 */

/** O adaptador da pill para o PEDIDO DO HOTEL: a ocupação (a MESMA frase
 *  `roomSummary` da barra de busca — hóspedes primeiro) com o retrato do
 *  primeiro hóspede, o destino e o vão de datas. A pill é a da bancada
 *  (`HotelSummaryPill`); aqui só se traduz o valor da barra. */
function HotelSearchPill({
  search,
  onExpand,
}: Readonly<{ search: HotelSearchBarProps; onExpand: () => void }>): ReactNode {
  const { t, locale } = useGenUiI18n();
  const { value, copy } = search;
  // O primeiro hóspede pelo roster da barra (nome e foto); fora dele, o glifo.
  const lead = search.travelers.find((option) => option.id === value.travelerIds[0]);
  const summary: HotelSummary = {
    place: value.place,
    ...(value.checkin ? { checkin: value.checkin } : {}),
    ...(value.checkout ? { checkout: value.checkout } : {}),
    // Mesma derivação da `HotelSearchBar` que a pill expande: hóspedes NOMEADOS
    // (vaga vazia não conta) e piso 1 nos quartos — senão o resumo trocava de
    // número ao abrir a barra.
    guests: namedGuestCount(value),
    rooms: Math.max(1, value.rooms.length),
    ...(lead ? { lead: { name: lead.label, photoUrl: lead.photoUrl ?? null } } : {}),
  };
  return (
    <HotelSummaryPill
      summary={summary}
      copy={{
        aria: t('hotel.results.pillAria'),
        title: t('hotel.results.title'),
        occupancy: (guests, rooms) => copy.roomSummary(rooms, guests),
      }}
      locale={locale}
      readOnly={search.readOnly === true}
      onExpand={onExpand}
      search={{
        onSearch: () => {
          search.onSubmit(search.value);
        },
        label: t('hotel.results.searchAgain'),
      }}
    />
  );
}

/** A barra do topo: ✕ · pill (ou a faixa de busca expandida) · trailing, e a
 *  fila de chips do palco estreito na segunda linha (#3140) — no shell é a
 *  segunda linha do ShellBar, sob a pill. */
function HotelResultsBar({
  search,
  dialogRef,
  onChipsSlot,
  onClose,
}: Readonly<{
  search: HotelSearchBarProps;
  dialogRef: ResultsDialogLifecycleSlice['dialogRef'];
  onChipsSlot: (element: HTMLElement | null) => void;
  onClose: () => void;
}>): ReactNode {
  const { t } = useGenUiI18n();
  const { takeoverTrailing } = useGenUiConfig();
  const trail = useScreenBarTrail('hotel', onClose);
  const [editing, setEditing] = useState(false);
  const barRef = useRef<HTMLDivElement>(null);
  const pillRef = useRef<HTMLDivElement>(null);
  // O foco segue o gesto: expandir leva à CAIXA da faixa (`tabIndex=-1`, o Tab
  // seguinte cai no primeiro campo) — não ao primeiro botão dela, que é gatilho
  // de tooltip do Base UI e engole o Esc na captura; recolher devolve ao
  // primeiro segmento da pill (o nó dela remonta, por isso via ref).
  // Só nas TROCAS: ao montar, o foco é da tela em diálogo (o popup, para o
  // leitor de tela anunciá-la), não da pill. Pelo valor anterior, não por uma
  // flag de primeira execução, que o setup duplo do StrictMode furaria.
  const wasEditingRef = useRef(editing);
  useEffect(() => {
    if (wasEditingRef.current === editing) return;
    wasEditingRef.current = editing;
    if (editing) barRef.current?.focus();
    // No celular a pill é o resumo compacto, um botão só, sem `[data-seg]`.
    else pillRef.current?.querySelector<HTMLElement>('[data-seg], button')?.focus();
  }, [editing]);
  const collapse = () => {
    setEditing(false);
  };
  // Esc com a faixa expandida é DELA (recolhe), não do dialog — salvo com um
  // painel da própria faixa (calendário, quartos) aberto por cima, que é quem
  // fecha primeiro. O `preventDefault` faz os dois listeners do ciclo de vida
  // (o local e o do documento) pularem.
  const onKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (!editing || event.key !== 'Escape' || event.defaultPrevented) return;
    if (hasForegroundDialog(dialogRef.current)) return;
    event.preventDefault();
    collapse();
  };
  return (
    <ScreenBar onKeyDown={onKeyDown}>
      <ScreenBarStart>
        <ScreenBarClose label={t('hotel.results.close')} onClick={onClose} />
        {trail}
      </ScreenBarStart>
      <ScreenBarCenter>
        {editing ? (
          <div
            ref={barRef}
            tabIndex={-1}
            className="flex min-w-0 flex-1 items-center gap-2 outline-none"
          >
            <div className="min-w-0 flex-1 [&>div]:max-w-2xl">
              <HotelSearchBar
                {...search}
                onSubmit={(value) => {
                  search.onSubmit(value);
                  collapse();
                }}
              />
            </div>
            <Button type="button" variant="ghost" size="sm" onClick={collapse}>
              {t('hotel.results.cancelEdit')}
            </Button>
          </div>
        ) : (
          <div ref={pillRef} className="min-w-0">
            <HotelSearchPill
              search={search}
              onExpand={() => {
                setEditing(true);
              }}
            />
          </div>
        )}
      </ScreenBarCenter>
      <ScreenBarEnd>{takeoverTrailing}</ScreenBarEnd>
      <ScreenBarRow ref={onChipsSlot} />
    </ScreenBar>
  );
}

/**
 * A tela em diálogo "Ver todos os hotéis", montada pelo organismo quando a
 * lista abre (`lifecycle.expanded`). Mantém o marcador
 * `data-hotel-all-results-dialog`, que o card procura para devolver o foco e a
 * pilha lógica (`hasForegroundDialog`) lê. O que abre POR CIMA dela — o mapa
 * cheio, a gaveta de detalhes — vem em `nested` e monta dentro dela: diálogo
 * do Base UI aninhado, nunca irmão.
 */
function HotelResultsDialog({
  lifecycle,
  fullscreenMap,
  view,
  list,
  ui,
  search,
  locked,
  detailsLocked,
  expiredRetry,
  onOpenDetails,
  title,
  nested,
}: ResultsDialogSharedProps & {
  readonly lifecycle: ResultsDialogLifecycleSlice;
  readonly fullscreenMap: { readonly open: () => void };
  readonly title: string;
  /** Travamento só da NAVEGAÇÃO das linhas: navegar não é escrever. */
  readonly detailsLocked: boolean;
  /** Ver `useExpiredSearchRetry`: em que pé está a busca vencida. */
  readonly expiredRetry: ExpiredSearchRetry;
  /** As camadas abertas por cima da lista. */
  readonly nested?: ReactNode;
}): ReactNode {
  // O slot da fila de chips do palco ESTREITO, na segunda linha da barra.
  // Estado (e não ref) porque a página o recebe como prop e porta nele: a tela
  // cobre o ShellBar do shell, e portar os chips no slot dele os deixaria sob
  // ela.
  const [chipsSlot, setChipsSlot] = useState<HTMLElement | null>(null);
  const config = useGenUiConfig();
  if (!lifecycle.expanded) return null;
  return (
    <ScreenDialog
      label={title}
      onClose={lifecycle.close}
      popupRef={lifecycle.dialogRef}
      attributes={{ 'data-hotel-all-results-dialog': 'true' }}
    >
      <HotelResultsBar
        search={search}
        dialogRef={lifecycle.dialogRef}
        onChipsSlot={setChipsSlot}
        onClose={lifecycle.close}
      />
      {/* A caixa trava a altura abaixo da barra e a página a enche: no largo a
          lista rola no próprio `<main>` (`listScroll="own"`, #4587), com a barra
          ao lado dela; no estreito o palco map-centric rola por dentro. O
          `overflow-y-auto` fica de guarda para o que passar disso. */}
      <div
        className="flex min-h-0 flex-1 flex-col overflow-y-auto"
        style={{ '--hui-results-page-chrome': SCREEN_BAR_HEIGHT } as React.CSSProperties}
      >
        <DeterministicResultsPage
          host={{ ...config, chipsContainer: chipsSlot }}
          view={view}
          list={list}
          effectiveUi={ui}
          searchValue={search.value}
          search={search}
          locks={{ panelLocked: locked, detailsLocked }}
          expiredRetry={expiredRetry}
          onMapExpand={fullscreenMap.open}
          onOpenDetails={onOpenDetails}
          // Sem compartilhar nesta tela: na conversa ele mora no rodapé do
          // card, ao lado do "Ver todos os hotéis" (port `hotelSearchShare`, #5156).
          share={undefined}
        />
      </div>
      {nested}
    </ScreenDialog>
  );
}

/**
 * As telas em diálogo do organismo e a gaveta do host, cada uma montada DENTRO
 * da que estiver por baixo (diálogos do Base UI aninhados, nunca irmãos): a
 * lista por baixo de tudo; o mapa cheio dentro dela, ou sozinho quando abre
 * direto do card ou da bancada; a gaveta de detalhes dentro do mapa, senão da
 * lista. Sem nenhuma das duas aberta, a gaveta não é daqui: o card a monta
 * fora da chave do organismo.
 */
export function HotelResultsLayers({
  dialogs,
  view,
  list,
  ui,
  search,
  locks,
  expiredRetry,
  mapProps,
  onOpenDetails,
  detailsLayer,
}: Readonly<{
  dialogs: {
    readonly allHotels: ResultsDialogLifecycleSlice;
    readonly fullscreenMap: ResultsDialogLifecycleSlice & { readonly open: () => void };
  };
  view: ResultsDialogSharedProps['view'];
  list: ResultsDialogSharedProps['list'];
  ui: ResultsDialogSharedProps['ui'];
  search: ResultsDialogSharedProps['search'];
  locks: { readonly panelLocked: boolean; readonly detailsLocked: boolean };
  expiredRetry: ExpiredSearchRetry;
  mapProps: ReturnType<typeof fullscreenPortalProps>;
  onOpenDetails: (hotelId: string) => void;
  detailsLayer: ReactNode;
}>): ReactNode {
  const config = useGenUiConfig();
  const { t } = useGenUiI18n();
  const listOpen = dialogs.allHotels.expanded;
  const mapOpen = dialogs.fullscreenMap.expanded;
  const map = (
    <HotelMapDialog
      host={config}
      lifecycle={dialogs.fullscreenMap}
      view={view}
      list={list}
      ui={ui}
      search={search}
      locked={locks.panelLocked}
      {...mapProps}
      onOpenDetails={onOpenDetails}
      title={t('hotel.map.aria')}
      nested={detailsLayer}
    />
  );
  return (
    <>
      <HotelResultsDialog
        lifecycle={dialogs.allHotels}
        fullscreenMap={dialogs.fullscreenMap}
        view={view}
        list={list}
        ui={ui}
        search={search}
        locked={locks.panelLocked}
        detailsLocked={locks.detailsLocked}
        expiredRetry={expiredRetry}
        onOpenDetails={onOpenDetails}
        title={t('hotel.results.title')}
        nested={
          <>
            {map}
            {!mapOpen && detailsLayer}
          </>
        }
      />
      {!listOpen && map}
    </>
  );
}
